/**
 * t536u: reporting messages and people, blocking someone's direct messages, and "Delete my account" with an Owner's
 * review. One set of wording and rules for desktop web, phone web and the iOS app; the API checks everything again.
 */

// ------------------------------------------------------------------------------------------------ reports
export type ReportReason = 'spam' | 'harassment' | 'inappropriate' | 'other';
export const REPORT_REASONS: { key: ReportReason; label: string }[] = [
  { key: 'spam', label: 'Spam' },
  { key: 'harassment', label: 'Harassment or bullying' },
  { key: 'inappropriate', label: 'Inappropriate content' },
  { key: 'other', label: 'Something else' },
];
export const reportReasonLabel = (r: string) => REPORT_REASONS.find(x => x.key === r)?.label ?? 'Something else';
export const REPORT_DETAILS_MAX = 1000;
/** Reports one person can send in an hour (the API also rate-limits the endpoint). */
export const REPORTS_PER_HOUR = 10;

export type ReportTargetType = 'message' | 'person';
export interface ContentReportInput { target_type: ReportTargetType; message_id?: string; user_id?: string; reason: ReportReason; details?: string }
export interface ContentReportResult { id: string; already?: true }

export const REPORT_TEXT = {
  menu: 'Report',
  titleMessage: 'Report message',
  titlePerson: (name: string) => `Report ${name}`,
  leadMessage: 'Let the people who look after this workspace know about this message. They’ll take a look.',
  leadPerson: 'Let the people who look after this workspace know. They’ll take a look.',
  private: 'Your name is only shown to the people who review reports. The person isn’t told who reported them.',
  reason: 'Reason',
  details: 'Details (optional)',
  detailsPlaceholder: 'Anything that would help them understand',
  send: 'Send report',
  cancel: 'Cancel',
  sent: 'Report sent. Thanks for letting us know.',
  already: 'You’ve already reported this. It’s with the reviewers.',
  failed: 'Couldn’t send the report. Try again.',
} as const;

export type ReportStatus = 'open' | 'dismissed' | 'message_deleted' | 'person_deactivated';
export const REPORT_STATUS_LABEL: Record<ReportStatus, string> = {
  open: 'Open', dismissed: 'Dismissed', message_deleted: 'Message deleted', person_deactivated: 'Person deactivated',
};
export interface ReportContextMessage { id: string; sender_name: string | null; body: string; created_at: string; deleted: boolean; reported?: boolean }
export interface ContentReport {
  id: string;
  target_type: ReportTargetType;
  reason: ReportReason;
  details: string | null;
  status: ReportStatus;
  created_at: string;
  resolved_at: string | null;
  resolved_by_name: string | null;
  reporter: { user_id: string | null; display_name: string | null };
  /** Who wrote the message, or the person reported. */
  reported: { user_id: string | null; display_name: string | null; active: boolean };
  message: null | {
    id: string; body: string; snapshot: string | null; deleted: boolean; created_at: string;
    /** Where it is: "#general", "Direct message", "Comments on an announcement" … */
    where: string;
    /** Relative app link to the message (or its item), or null when it has gone. */
    link: string | null;
    context: ReportContextMessage[];
  };
}
export const MODERATION_TEXT = {
  title: 'Moderation',
  empty: 'No open reports. Reports people send about messages or people show here.',
  emptyClosed: 'Nothing closed yet.',
  open: 'Open', closed: 'Closed',
  dismiss: 'Dismiss', deleteMessage: 'Delete message', messageReporter: 'Message the reporter', deactivate: 'Deactivate person',
  deleteConfirm: 'Delete this message for everyone? This can’t be undone.',
  dismissed: 'Report dismissed', deleted: 'Message deleted', deactivated: 'Deactivated',
  reportedBy: 'Reported by', reason: 'Reason', details: 'Details', when: 'Reported',
  openInChat: 'Open in chat',
} as const;

// ------------------------------------------------------------------------------------------------ blocks
export const BLOCK_TEXT = {
  block: 'Block', unblock: 'Unblock',
  title: (name: string) => `Block ${name}?`,
  body: 'You won’t see your direct messages with them in your chat list and won’t get notifications from them. They aren’t told. You’ll still see their messages in shared channels. You can unblock them here any time.',
  confirm: 'Block', cancel: 'Cancel',
  blocked: (name: string) => `${name} blocked`, unblocked: (name: string) => `${name} unblocked`,
  note: 'Blocked: their direct messages are hidden and you get no notifications from them.',
} as const;

// ------------------------------------------------------------------------------------------------ delete my account
export const ACCOUNT_DELETE_CONFIRM_WORD = 'DELETE';
/** The confirm box takes their sign-in email (any case) or the word DELETE. */
export function accountDeleteConfirmMatches(typed: string, email: string | null | undefined): boolean {
  const t = (typed ?? '').trim();
  if (!t) return false;
  if (t === ACCOUNT_DELETE_CONFIRM_WORD) return true;
  return Boolean(email) && t.toLowerCase() === String(email).trim().toLowerCase();
}

export type DeletionRequestStatus = 'pending' | 'approved' | 'declined' | 'cancelled';
export interface MyDeletionRequest {
  request: null | { id: string; status: DeletionRequestStatus; requested_at: string; decided_at: string | null; decision_note: string | null };
  /** The only active Owner can't ask (they transfer ownership first). */
  last_owner: boolean;
  /** Re-entering a password is how they confirm; people without one (single sign-on only) ask their Owner instead. */
  password_auth: boolean;
  email: string | null;
}
export interface DeletionRequestRow {
  id: string; user_id: string | null; display_name: string | null; email: string | null; active: boolean;
  status: DeletionRequestStatus; requested_at: string; decided_at: string | null; decided_by_name: string | null; decision_note: string | null;
  /** Waiting 7 days or more. */
  overdue: boolean;
}
export interface DeletionRequestList { requests: DeletionRequestRow[]; can_decide: boolean }
export const DELETION_REMINDER_DAYS = 7;

export const ACCOUNT_DELETE_TEXT = {
  item: 'Delete my account',
  title: 'Delete my account',
  lead: 'You can ask for your account to be deleted. An owner of this workspace reviews the request first.',
  whatHappens: 'When an owner approves it:',
  points: [
    'Your account is deleted and you can no longer sign in.',
    'Your personal details (name, email, phone, photo and settings) are removed.',
    'Your HR record, documents, bank details and emergency contacts are removed.',
    'Messages and comments you posted stay for others but show as from “Deleted user”.',
  ],
  kept: 'Records your employer has to keep by law stay with them: your shifts, timesheets, time off and pay history are kept without your name, and payroll files they have already made are not changed.',
  elsewhere: 'If you’re in more than one workspace, only this workspace’s account is deleted.',
  review: 'Nothing is deleted until an owner approves. You can cancel the request until then.',
  confirmLabel: (email: string | null) => (email ? `Type your email (${email}) or DELETE to confirm` : 'Type DELETE to confirm'),
  passwordLabel: 'Your password',
  passwordHint: 'Enter your password to confirm it’s you.',
  noPassword: 'You sign in without a password here, so we can’t confirm it’s you on this screen. Ask an owner of your workspace to delete your account.',
  continue: 'Continue',
  submit: 'Request deletion',
  cancel: 'Cancel',
  requested: (day: string) => `Deletion requested on ${day}`,
  requestedNote: 'An owner will review it. Nothing is deleted until they approve.',
  cancelRequest: 'Cancel request',
  cancelled: 'Request cancelled',
  sent: 'Request sent. An owner will review it.',
  declined: (day: string) => `Your request on ${day} wasn’t approved.`,
  lastOwner: 'You’re the only owner of this workspace, so your account can’t be deleted yet. Transfer ownership to someone else first (Settings > Users), then ask again.',
  wrongPassword: 'That password isn’t right.',
} as const;

export const DELETION_QUEUE_TEXT = {
  title: 'Deletion requests',
  empty: 'No account deletion requests.',
  pending: 'Waiting for review', history: 'Earlier',
  approve: 'Approve and delete', decline: 'Decline',
  ownersOnly: 'Only owners can approve or decline these.',
  waiting: (days: number) => (days < 1 ? 'Asked today' : `Waiting ${days} day${days === 1 ? '' : 's'}`),
  deactivateFirst: 'They’re still active, so they’ll be deactivated first.',
  declineTitle: (name: string) => `Decline ${name}’s request?`,
  declineLabel: 'Message to them (optional)',
  declineHint: 'Emailed to them with the decision. Their account stays as it is.',
  declined: 'Request declined',
  banner: (n: number) => `${n} account deletion request${n === 1 ? ' is' : 's are'} waiting for your review`,
  review: 'Review',
} as const;
